import type {
    HistoryBucket,
    HistoryPatientCounts,
    HistorySeriesPoint
} from '../../../services/dmMonitoringOverviewHistory';
import { parseBucketDate } from '../../../utils/chartFormatting.ts';
import type { GranularityOption } from '../../accounts/GranularityToggle';
import type { PerPersonMeasure } from './perPersonRows.ts';

export type ActivityScope = 'notifications' | 'messages' | 'both';

export const ACTIVITY_SCOPE_OPTIONS: readonly { key: ActivityScope; label: string }[] = [
    { key: 'notifications', label: 'Notifications' },
    { key: 'messages', label: 'Messages' },
    { key: 'both', label: 'Both' }
];

export const MEASURE_OPTIONS: GranularityOption<PerPersonMeasure>[] = [
    { key: 'events', label: 'Items' },
    { key: 'patients', label: 'Patients' }
];

export interface ActivityRow {
    bucketStart: string;
    label: string;
    raised: number;
    resolved: number;
}

const LABEL_LOCALE = 'en-US';

// Bucket starts are calendar dates in the request's zone, so they are read and printed as UTC.
const BUCKET_FORMATS: Readonly<Record<HistoryBucket, Intl.DateTimeFormatOptions>> = {
    day: { month: 'short', day: 'numeric', timeZone: 'UTC' },
    week: { month: 'short', day: 'numeric', timeZone: 'UTC' },
    month: { month: 'short', year: 'numeric', timeZone: 'UTC' }
};

export function bucketLabel(bucketStart: string, bucket: HistoryBucket): string {
    const date = parseBucketDate(bucketStart);
    if (!date) return bucketStart;
    const text = date.toLocaleDateString(LABEL_LOCALE, BUCKET_FORMATS[bucket]);
    return bucket === 'week' ? `Week of ${text}` : text;
}

type ActivityCounts = Pick<ActivityRow, 'raised' | 'resolved'>;
type CountPick = (point: HistorySeriesPoint) => ActivityCounts;

const SCOPE_COUNTS: Readonly<Record<ActivityScope, CountPick>> = {
    notifications: (point) => ({
        raised: point.notificationsRaised,
        resolved: point.notificationsResolved
    }),
    messages: (point) => ({ raised: point.messagesRaised, resolved: point.messagesResolved }),
    both: (point) => ({
        raised: point.notificationsRaised + point.messagesRaised,
        resolved: point.notificationsResolved + point.messagesResolved
    })
};

// Both takes the distinct totals, since a patient with a notification and a message counts once.
const PATIENT_SCOPE_COUNTS: Readonly<
    Record<ActivityScope, (patients: HistoryPatientCounts) => ActivityCounts>
> = {
    notifications: (patients) => ({
        raised: patients.notificationsRaised,
        resolved: patients.notificationsResolved
    }),
    messages: (patients) => ({
        raised: patients.messagesRaised,
        resolved: patients.messagesResolved
    }),
    both: (patients) => ({ raised: patients.raisedTotal, resolved: patients.resolvedTotal })
};

function hasPatientCounts(
    point: HistorySeriesPoint
): point is HistorySeriesPoint & { patients: HistoryPatientCounts } {
    return point.patients !== undefined;
}

function activityRow(point: HistorySeriesPoint, bucket: HistoryBucket, counts: ActivityCounts) {
    return {
        bucketStart: point.bucketStart,
        label: bucketLabel(point.bucketStart, bucket),
        ...counts
    };
}

// Null when patients are asked for but the backend didn't send them.
export function activityRows(
    series: readonly HistorySeriesPoint[],
    bucket: HistoryBucket,
    scope: ActivityScope,
    measure: PerPersonMeasure
): ActivityRow[] | null {
    if (measure === 'events') {
        return series.map((point) => activityRow(point, bucket, SCOPE_COUNTS[scope](point)));
    }
    if (!series.every(hasPatientCounts)) return null;
    return series.map((point) =>
        activityRow(point, bucket, PATIENT_SCOPE_COUNTS[scope](point.patients))
    );
}

export function hasActivity(rows: readonly ActivityRow[]): boolean {
    return rows.some((row) => row.raised > 0 || row.resolved > 0);
}
